/**
 * Catálogo de tribunais no painel: tipos e funções puras (sem React, sem rede).
 *
 * Os tipos espelham o contrato C1 (backend/app/catalog/__init__.py):
 * `match: [{host, path_prefix?}]`, `sso: [{host, params}]` e `grau` "1g" | "2g" | null
 * (null = sistema que não separa instância; a `key` fica sem grau).
 * O painel só LÊ o catálogo; quem manda nele é o backend.
 */

export type Grau = '1g' | '2g';

export interface CatalogMatchRule {
  host: string;
  path_prefix?: string | null;
}

export interface CatalogSsoRule {
  host: string;
  params: Record<string, string>;
}

export interface CatalogCourt {
  key: string;
  system: string;
  tribunal: string;
  uf: string | null;
  grau: Grau | null;
  label: string;
  login_url: string;
  match: CatalogMatchRule[];
  sso: CatalogSsoRule[];
  // Anotações internas: o servidor não envia mais (ficam só no courts.json).
  verified_at?: string;
  notes?: string;
}

export interface Catalog {
  version: number;
  systems: Record<string, { label: string }>;
  courts: CatalogCourt[];
}

export interface SystemGroup {
  system: string;
  label: string;
  courts: CatalogCourt[];
}

/** Um tribunal dentro de um sistema (o 1º e o 2º grau ficam juntos em `courts`). */
export interface TribunalOption {
  id: string;
  label: string;
  uf: string | null;
  courts: CatalogCourt[];
}

const ORDEM_DOS_GRAUS: Record<string, number> = { '1g': 1, '2g': 2 };

export function grauLabel(grau: Grau | null): string {
  if (grau === '1g') return '1º grau';
  if (grau === '2g') return '2º grau';
  return '';
}

/** Sistemas na ordem do catálogo, cada um com seus tribunais; sistema sem tribunal fica de fora. */
export function groupBySystem(catalog: Catalog): SystemGroup[] {
  return Object.keys(catalog.systems)
    .map((system) => ({
      system,
      label: catalog.systems[system].label,
      courts: catalog.courts.filter((court) => court.system === system),
    }))
    .filter((grupo) => grupo.courts.length > 0);
}

/** Tribunais de um sistema, sem repetição (1º e 2º grau juntos), ordenados por UF e depois por sigla. */
export function listTribunals(catalog: Catalog, system: string): TribunalOption[] {
  const porTribunal = new Map<string, CatalogCourt[]>();
  for (const court of catalog.courts) {
    if (court.system !== system) continue;
    const lista = porTribunal.get(court.tribunal) ?? [];
    lista.push(court);
    porTribunal.set(court.tribunal, lista);
  }

  const opcoes: TribunalOption[] = [];
  porTribunal.forEach((courts, id) => {
    const uf = courts.find((court) => court.uf)?.uf ?? null;
    opcoes.push({
      id,
      uf,
      label: `${uf ?? '—'} · ${id.toUpperCase()}`,
      courts: [...courts].sort((a, b) => (ORDEM_DOS_GRAUS[a.grau ?? ''] ?? 0) - (ORDEM_DOS_GRAUS[b.grau ?? ''] ?? 0)),
    });
  });

  return opcoes.sort((a, b) => {
    if (a.uf !== b.uf) {
      if (a.uf === null) return 1;
      if (b.uf === null) return -1;
      return a.uf.localeCompare(b.uf, 'pt-BR');
    }
    return a.id.localeCompare(b.id, 'pt-BR');
  });
}

/** Só pergunta o grau quando o tribunal tem mais de uma opção (grau null ou grau único: sem seletor). */
export function tribunalNeedsGrau(tribunal: TribunalOption): boolean {
  return tribunal.courts.length > 1;
}

export function findCourt(catalog: Catalog | null, key: string | null | undefined): CatalogCourt | null {
  if (!catalog || !key) return null;
  return catalog.courts.find((court) => court.key === key) ?? null;
}

/** Rótulo sugerido para a credencial ao escolher o tribunal (ex.: "PJe · TJMG · 1º grau"). */
export function suggestedLabel(court: CatalogCourt): string {
  return court.label;
}

/** Rótulo do tribunal para Card/Row; null quando não há vínculo ou o catálogo ainda não chegou. */
export function courtLabel(catalog: Catalog | null, key: string | null | undefined): string | null {
  const court = findCourt(catalog, key);
  return court ? court.label : null;
}

/**
 * Novo rótulo ao escolher um tribunal: só preenche se estiver vazio ou se ainda for
 * exatamente a sugestão anterior. Texto digitado pelo usuário nunca é sobrescrito.
 */
export function nextLabel(current: string, previousSuggestion: string | null, court: CatalogCourt | null): string {
  if (!court) return current;
  if (current.trim() === '' || (previousSuggestion !== null && current === previousSuggestion)) {
    return suggestedLabel(court);
  }
  return current;
}

interface PadraoLido {
  host: string;
  path: string;
  temCaminho: boolean;
  params: URLSearchParams;
}

// Mesma limpeza que o Card usa para abrir a URL: tira "*." do início e "*" ou "/*" do fim.
function lerPadrao(bruto: string): PadraoLido | null {
  let limpo = bruto.trim().replace(/^\*\./, '').replace(/\/?\*$/, '');
  if (!limpo) return null;
  if (!/^https?:\/\//i.test(limpo)) limpo = 'https://' + limpo;
  try {
    const url = new URL(limpo);
    if (!url.hostname) return null;
    return { host: url.hostname.toLowerCase(), path: url.pathname, temCaminho: url.pathname !== '/', params: url.searchParams };
  } catch {
    return null;
  }
}

type Evidencia = 'forte' | 'fraca';

function evidenciaDoTribunal(court: CatalogCourt, padrao: PadraoLido): Evidencia | null {
  let melhor: Evidencia | null = null;

  for (const regra of court.match) {
    if (regra.host !== padrao.host) continue;
    if (!regra.path_prefix) return 'forte';
    if (!padrao.temCaminho) {
      melhor = 'fraca'; // só o host: não dá para saber o caminho
    } else {
      const caminho = padrao.path.endsWith('/') ? padrao.path : padrao.path + '/';
      if (caminho.startsWith(regra.path_prefix)) return 'forte';
    }
  }

  for (const regra of court.sso) {
    if (regra.host !== padrao.host) continue;
    const parametros = Object.entries(regra.params);
    // SSO compartilhado (ex.: sso.cloud.pje.jus.br) só vale com os parâmetros do tribunal na URL.
    if (parametros.every(([nome, valor]) => padrao.params.get(nome) === valor)) return 'forte';
  }

  return melhor;
}

/**
 * Tribunais do catálogo que combinam com as URLs dadas, usando as regras reais:
 * host + path_prefix de `match`, ou host + params de `sso`. Pode devolver mais de um
 * (ex.: 1º e 2º grau no mesmo endereço); a escolha é sempre do usuário.
 * Se algum candidato for "forte", os "fracos" (só o host bateu) são descartados.
 */
export function suggestCourts(catalog: Catalog, urls: string[]): CatalogCourt[] {
  const padroes = urls.map(lerPadrao).filter((p): p is PadraoLido => p !== null);
  if (padroes.length === 0) return [];

  const fortes: CatalogCourt[] = [];
  const fracos: CatalogCourt[] = [];
  for (const court of catalog.courts) {
    const evidencias = padroes.map((padrao) => evidenciaDoTribunal(court, padrao));
    if (evidencias.includes('forte')) fortes.push(court);
    else if (evidencias.includes('fraca')) fracos.push(court);
  }
  return fortes.length > 0 ? fortes : fracos;
}

export interface CamposOutroSite {
  system_type: string;
  tribunal_slug: string;
  url_patterns: string[];
}

/**
 * Campos de "onde fica o sistema" do payload da credencial.
 * Com tribunal: só `catalog_key` (o backend deriva system_type, tribunal_slug e url_patterns).
 * "Outro site": `catalog_key: null` + os campos de sempre.
 */
export function buildCourtFields(catalogKey: string | null, outroSite: CamposOutroSite): Record<string, unknown> {
  if (catalogKey) return { catalog_key: catalogKey };
  return { catalog_key: null, ...outroSite };
}

/**
 * Auditoria M2: o subtítulo do cartão mostra só o que o título ainda não diz.
 * Título "eproc TRF6 1º grau" + "eproc · TRF6 · 1º grau" → null; título "TRF6 - Dr. Silva" → "eproc · 1º grau".
 */
export function subtituloSemRepetir(titulo: string, subtitulo: string | null | undefined): string | null {
  if (!subtitulo) return null;
  const normalizar = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const tituloNormalizado = normalizar(titulo);
  const partes = subtitulo.split(' · ').filter((parte) => !tituloNormalizado.includes(normalizar(parte)));
  return partes.length > 0 ? partes.join(' · ') : null;
}
